// InfoCard.tsx
import React, { PropsWithChildren, useState } from 'react';
import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type InfoCardDossierProps = {
    label: string;
    sx?: SxProps;
    onClick?: () => void;
    actionIcon?: IconDefinition;
    actionIconOption?: IconDefinition;
};

/**
 * Composant de carte d'information affichant un label et du contenu enfant.
 * Supporte les interactions via onClick avec indicateur visuel et icône d'action optionnelle.
 * L'icône d'action n'est visible qu'au survol pour une interface plus épurée.
 */
const InfoCardDossier: React.FunctionComponent<PropsWithChildren<InfoCardDossierProps>> =
    function ({ label, sx = {}, onClick, actionIcon, actionIconOption, children }) {
        const { palette } = useTheme();
        const [isHovered, setIsHovered] = useState(false);

        const isInteractive = Boolean(onClick);

        const labelSx = {
            fontSize: '12px',
            fontWeight: '500',
            lineHeight: '19.2px',
            color: palette.grey[500],
        };

        const containerSx = {
            ...sx,
            ...(typeof onclick !== 'undefined' && { cursor: 'pointer' }),
            ...(isInteractive && {
                cursor: 'pointer',
                transition: 'background-color 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: palette.grey[100],
                },
                '&:active': {
                    backgroundColor: palette.grey[200],
                },
            }),
        };

        const contentBoxSx = {
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 0',
        };

        const handleClick = () => {
            if (onClick) {
                onClick();
            }
        };

        const handleKeyPress = (event: React.KeyboardEvent) => {
            if (onClick && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onClick();
            }
        };

        const handleMouseEnter = () => {
            setIsHovered(true);
        };

        const handleMouseLeave = () => {
            setIsHovered(false);
        };

        return (
            <Box
                sx={containerSx}
                onClick={handleClick}
                onKeyPress={handleKeyPress}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                tabIndex={isInteractive ? 0 : undefined}
                role={isInteractive ? 'button' : undefined}
            >
                <Typography
                    component="span"
                    sx={labelSx}
                >
                    {label}
                </Typography>
                <Box sx={contentBoxSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>{children}</Box>
                    {isInteractive && actionIcon && (
                        <Box
                            sx={{
                                marginLeft: '8px',
                                opacity: isHovered ? 1 : 0,
                                transition: 'opacity 0.2s ease-in-out',
                            }}
                        >
                            <FaOptionIcon
                                icon={actionIcon}
                                option={actionIconOption}
                                sx={{
                                    fontSize: '16px',
                                    color: palette.grey[600],
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

export default InfoCardDossier;
